import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { UpgradeReviewDetail } from '../features/recipe-upgrades/review-detail'
import {
    upgradeAllRecipes,
    getUpgradeReview,
    upgradeRecipe,
} from '../features/recipe-upgrades/review-client'
import { REVIEW_COPY } from '../features/recipe-upgrades/review-copy'
import { UpgradeReviewTable } from '../features/recipe-upgrades/review-table'
import { UpgradeReviewTally } from '../features/recipe-upgrades/review-tally'
import type {
    UpgradeReviewResponse,
    UpgradeReviewRow,
} from '../features/recipe-upgrades/review-types'
import type { State } from '../reducers'

const errorCode = (error: any): string | undefined =>
    error?.response?.data?.code || error?.data?.code

export const RecipeUpgradeReview = () => {
    const [searchParams] = useSearchParams()
    const navigate = useNavigate()
    const recipeId = searchParams.get('recipe_id') || undefined
    // The ProfileId request header is set by the user reducer. Requests sent before it is
    // set resolve to the account's default profile, so wait for the selected profile.
    const profile = useSelector<State, unknown>((state) => state.user.profile)
    const [review, setReview] = useState<UpgradeReviewResponse | null>(null)
    const [selectedId, setSelectedId] = useState<string | undefined>(recipeId)
    const [loading, setLoading] = useState(true)
    const [upgrading, setUpgrading] = useState(false)
    const [upgradeError, setUpgradeError] = useState<string | null>(null)
    const [acceptedIds, setAcceptedIds] = useState<string[] | null>(null)

    const load = useCallback(async () => {
        const result = await getUpgradeReview(recipeId)
        setReview(result)
        setSelectedId((current) =>
            result.data.some((row) => row.recipe_id === current)
                ? current
                : result.data[0]?.recipe_id,
        )
        setLoading(false)
        return result
    }, [recipeId])

    useEffect(() => {
        if (!profile) return
        load().catch(() => setLoading(false))
    }, [load, profile])

    useEffect(() => {
        if (acceptedIds) return
        if (!review?.data.some((row) => row.status === 'pending')) return
        const timer = window.setInterval(() => load().catch(() => undefined), 5000)
        const timeout = window.setTimeout(() => window.clearInterval(timer), 120000)
        return () => {
            window.clearInterval(timer)
            window.clearTimeout(timeout)
        }
    }, [acceptedIds, review, load])

    useEffect(() => {
        if (!acceptedIds) return

        let active = true
        const completedCount = (result: UpgradeReviewResponse) =>
            acceptedIds.filter(
                (id) => result.data.find((row) => row.recipe_id === id)?.status === 'upgraded',
            ).length
        const poll = async (): Promise<UpgradeReviewResponse | undefined> => {
            const result = await load()
            if (!active) return
            const count = completedCount(result)
            if (count === acceptedIds.length) {
                navigate(`/segments?upgraded=${count}`)
            }
            return result
        }
        const interval = window.setInterval(() => poll().catch(() => undefined), 3000)
        const timeout = window.setTimeout(() => {
            poll()
                .then((result) => {
                    if (!active || !result) return
                    const count = completedCount(result)
                    if (count === acceptedIds.length) {
                        navigate(`/segments?upgraded=${count}`)
                    } else {
                        setUpgradeError(REVIEW_COPY.upgradeProgress(count, acceptedIds.length))
                        setUpgrading(false)
                        setAcceptedIds(null)
                    }
                })
                .catch(() => undefined)
        }, 180000)
        return () => {
            active = false
            window.clearInterval(interval)
            window.clearTimeout(timeout)
        }
    }, [acceptedIds, load, navigate])

    const selected = useMemo(
        () => review?.data.find((row) => row.recipe_id === selectedId),
        [review, selectedId],
    )
    const eligible = useMemo(
        () =>
            review?.data.filter((row) => row.status === 'ok' || row.status === 'differences') || [],
        [review],
    )

    const upgrade = async (row?: UpgradeReviewRow) => {
        let waitingForAllUpgrades = false
        setUpgrading(true)
        setUpgradeError(null)
        try {
            const target = row || selected
            if ((row || recipeId) && target) {
                await upgradeRecipe(target.recipe_id)
                navigate('/segments?upgraded=1')
            } else {
                const ids = eligible.map(({ recipe_id }) => recipe_id)
                await upgradeAllRecipes(ids)
                setAcceptedIds(ids)
                waitingForAllUpgrades = true
                return
            }
        } catch (error) {
            setUpgradeError(
                REVIEW_COPY.errors[errorCode(error) as keyof typeof REVIEW_COPY.errors] ||
                    "We couldn't upgrade these Recipes. Try again.",
            )
        } finally {
            if (!waitingForAllUpgrades) setUpgrading(false)
        }
    }

    if (loading)
        return <main className="p-8 text-sm text-[#5F6B7C]">Loading your upgrade review…</main>
    if (!review)
        return (
            <main className="p-8 text-sm text-[#AC2F33]">
                We couldn't load your upgrade review.
            </main>
        )
    if (!review.data.length)
        return <main className="p-8 text-sm text-[#5F6B7C]">{REVIEW_COPY.noRecipes}</main>

    const single = Boolean(recipeId)
    const showAction =
        !single || (!!selected && eligible.some((row) => row.recipe_id === selected.recipe_id))
    const actionLabel = upgrading
        ? 'Upgrading…'
        : single
          ? 'Upgrade Recipe'
          : `Upgrade ${eligible.length} Ready Recipe${eligible.length === 1 ? '' : 's'}`
    const disabled =
        upgrading ||
        review.tally.pending > 0 ||
        (recipeId
            ? !selected || !eligible.some((row) => row.recipe_id === selected.recipe_id)
            : !eligible.length)
    return (
        // The app shell clips route content, so this route scrolls itself.
        <main className="h-full overflow-y-auto bg-[#F8FAFC] px-8 pb-10 pt-7 text-[#1C2127]">
            <nav aria-label="Breadcrumb" className="mb-7 text-sm text-[#215DB0]">
                Automate <span className="mx-1 text-[#91969B]">/</span>
                <Link to="/recipes">Recipes</Link>
                <span className="mx-1 text-[#91969B]">/</span>
                <span className="font-semibold text-[#1C2127]">Upgrade to Segments</span>
            </nav>
            <div className="mb-7 flex items-end gap-8">
                <div className="flex-1">
                    <h1 className="m-0 text-[28px] font-semibold leading-[34px] tracking-[-0.01em]">
                        {single && selected
                            ? REVIEW_COPY.singleTitle(selected.name)
                            : REVIEW_COPY.title(review.tally.total)}
                    </h1>
                </div>
                <Link
                    to="/recipes"
                    className="h-9 rounded-lg border border-[#DEE2E6] bg-white px-5 py-2 text-sm text-[#1C2127] shadow-[0_2px_4px_rgba(0,0,0,.075)]"
                >
                    Back to Recipes
                </Link>
                {showAction && (
                    <button
                        type="button"
                        disabled={disabled}
                        onClick={() => upgrade()}
                        className="h-9 rounded-lg bg-[#2D72D2] px-4 text-sm text-white shadow-[0_2px_4px_rgba(0,0,0,.075)] disabled:bg-[#8F99A826] disabled:text-[#91969B]"
                    >
                        {actionLabel}
                    </button>
                )}
            </div>
            {upgradeError && (
                <div
                    role="alert"
                    className="mb-5 rounded-sm bg-[rgb(205_66_70_/_10%)] p-[15px] text-sm leading-[21px] text-[#AC2F33]"
                >
                    {upgradeError}
                </div>
            )}
            <div className="space-y-7">
                {!single && <UpgradeReviewTally tally={review.tally} rows={review.data} />}
                {!single && (
                    <UpgradeReviewTable
                        rows={review.data}
                        selectedId={selectedId}
                        onSelect={(row) => setSelectedId(row.recipe_id)}
                    />
                )}
                {selected && (
                    <UpgradeReviewDetail
                        row={selected}
                        onUpgrade={() => upgrade(selected)}
                        upgrading={upgrading}
                        showUpgrade={!single}
                    />
                )}
                {!single && (
                    <div className="flex items-center gap-6 rounded border-l-[3px] border-[#238551] bg-white p-[18px]">
                        <p className="m-0 flex-1 text-[15px] leading-[23px]">
                            {REVIEW_COPY.reassurance}
                        </p>
                        <button
                            type="button"
                            disabled={disabled}
                            onClick={() => upgrade()}
                            className="h-9 rounded-lg bg-[#2D72D2] px-6 text-sm text-white shadow-[0_2px_4px_rgba(0,0,0,.075)] disabled:bg-[#8F99A826] disabled:text-[#91969B]"
                        >
                            {actionLabel}
                        </button>
                    </div>
                )}
            </div>
        </main>
    )
}
